/**
* Client-side logging utility that respects production environment
* Disables debug logs in production to improve performance and security
*/

class ClientLogger {
    constructor() {
        // Detect environment
        this.isProduction = this.detectProduction();
        this.isDevelopment = !this.isProduction;

        // Log levels
        this.levels = {
            ERROR: 0,
            WARN: 1,
            INFO: 2,
            DEBUG: 3
        };

        // Current log level (DEBUG in development, ERROR in production)
        this.currentLevel = this.isProduction ? this.levels.ERROR : this.levels.DEBUG;

        // Console methods backup
        this.originalConsole = {
            log: console.log,
            error: console.error,
            warn: console.warn,
            info: console.info,
            debug: console.debug
        };

        // Override console methods if in production
        if (this.isProduction)
        {
            this.overrideConsoleMethods();
        }
    }

    detectProduction() {
        // Check multiple indicators for production environment
        return (
        window.location.hostname !== 'localhost' &&
        window.location.hostname !== '127.0.0.1' &&
        !window.location.hostname.includes('dev') &&
        !window.location.hostname.includes('test') &&
        !window.location.hostname.includes('staging') &&
        window.location.protocol === 'https:' &&
        !window.location.search.includes('debug=true')
        );
    }

    overrideConsoleMethods() {
        const self = this;

        // Override console.log to only show in development
        console.log = function(...args) {
            if (self.isDevelopment)
            {
                self.originalConsole.log.apply(console, args);
            }
        };

        // Override console.debug to only show in development
        console.debug = function(...args) {
            if (self.isDevelopment)
            {
                self.originalConsole.debug.apply(console, args);
            }
        };

        // Override console.info to only show in development
        console.info = function(...args) {
            if (self.isDevelopment)
            {
                self.originalConsole.info.apply(console, args);
            }
        };

        // Keep console.error and console.warn in production for critical issues
        // but limit their output
        console.error = function(...args) {
            self.originalConsole.error.apply(console, args);
        };

        console.warn = function(...args) {
            self.originalConsole.warn.apply(console, args);
        };
    }

    log(level, message, data = null, functionName = null) {
        if (this.levels[level] > this.currentLevel)
        {
            return; // Skip logging if level is too verbose
        }

        const timestamp = new Date().toISOString();
        const prefix = `[${timestamp}] [${level}]`;
        const functionPrefix = functionName ? `[${functionName}]` : '';
        const fullMessage = `${prefix} ${functionPrefix} ${message}`;

        if (this.isDevelopment)
        {
            switch (level)
            {
                case 'ERROR':
                this.originalConsole.error(fullMessage);
                if (data) this.originalConsole.error('Data:', data);
                break;
                case 'WARN':
                this.originalConsole.warn(fullMessage);
                if (data) this.originalConsole.warn('Data:', data);
                break;
                case 'INFO':
                this.originalConsole.info(fullMessage);
                if (data) this.originalConsole.info('Data:', data);
                break;
                case 'DEBUG':
                this.originalConsole.debug(fullMessage);
                if (data) this.originalConsole.debug('Data:', data);
                break;
            }
        }
        else
        {
            // In production, only log errors and warnings to console
            if (level === 'ERROR' || level === 'WARN')
            {
                this.originalConsole[level.toLowerCase()](fullMessage);
                if (data) this.originalConsole[level.toLowerCase()]('Data:', data);
            }
        }
    }

    error(message, data = null, functionName = null) {
        this.log('ERROR', message, data, functionName);
    }

    warn(message, data = null, functionName = null) {
        this.log('WARN', message, data, functionName);
    }

    info(message, data = null, functionName = null) {
        this.log('INFO', message, data, functionName);
    }

    debug(message, data = null, functionName = null) {
        this.log('DEBUG', message, data, functionName);
    }

    // PayPal-specific logging (with emojis for easy identification)
    paypalDebug(message, data = null) {
        this.debug(`🔄 ${message}`, data, 'PayPal');
    }

    paypalInfo(message, data = null) {
        this.info(`✅ ${message}`, data, 'PayPal');
    }

    paypalError(message, data = null) {
        this.error(`❌ ${message}`, data, 'PayPal');
    }

    paypalWarn(message, data = null) {
        this.warn(`⚠️ ${message}`, data, 'PayPal');
    }

    // Order-specific logging
    orderDebug(message, data = null) {
        this.debug(`📦 ${message}`, data, 'Order');
    }

    orderInfo(message, data = null) {
        this.info(`✅ ${message}`, data, 'Order');
    }

    orderError(message, data = null) {
        this.error(`❌ ${message}`, data, 'Order');
    }

    // Cart-specific logging
    cartDebug(message, data = null) {
        this.debug(`🛒 ${message}`, data, 'Cart');
    }

    cartInfo(message, data = null) {
        this.info(`✅ ${message}`, data, 'Cart');
    }

    cartError(message, data = null) {
        this.error(`❌ ${message}`, data, 'Cart');
    }

    // UI-specific logging
    uiDebug(message, data = null) {
        this.debug(`🎨 ${message}`, data, 'UI');
    }

    uiInfo(message, data = null) {
        this.info(`✅ ${message}`, data, 'UI');
    }

    uiError(message, data = null) {
        this.error(`❌ ${message}`, data, 'UI');
    }

    // Database-specific logging
    dbDebug(message, data = null) {
        this.debug(`🗄️ ${message}`, data, 'Database');
    }

    dbInfo(message, data = null) {
        this.info(`✅ ${message}`, data, 'Database');
    }

    dbError(message, data = null) {
        this.error(`❌ ${message}`, data, 'Database');
    }

    // Restore original console methods
    restore() {
        console.log = this.originalConsole.log;
        console.error = this.originalConsole.error;
        console.warn = this.originalConsole.warn;
        console.info = this.originalConsole.info;
        console.debug = this.originalConsole.debug;
    }

    // Get environment info
    getEnvironmentInfo() {
        return {
            isProduction: this.isProduction,
            isDevelopment: this.isDevelopment,
            currentLevel: Object.keys(this.levels)[this.currentLevel],
            hostname: window.location.hostname,
            protocol: window.location.protocol
        };
    }
}

// Create singleton instance
const clientLogger = new ClientLogger();

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports)
{
    module.exports = clientLogger;
}
else
{
    window.ClientLogger = clientLogger;
}
